import React, { useState } from 'react';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useData } from '../../context/DataContext';
import './ExportData.css';

const ExportData = () => {
  const { getAllData, hasExportableData } = useData();
  const [exportFormat, setExportFormat] = useState('pdf');
  const [selectedSections, setSelectedSections] = useState({
    calculator: true,
    scenarios: true,
    milestones: true,
    retirement: true
  });
  const [exportInProgress, setExportInProgress] = useState(false);
  const [userName, setUserName] = useState('');
  const [userNotes, setUserNotes] = useState('');

  const handleFormatChange = (e) => {
    setExportFormat(e.target.value);
  };

  const handleSectionToggle = (section) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value / 100);
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const exportToPDF = () => {
    try {
      setExportInProgress(true);
      const data = getAllData();
      
      // Criar o documento
      const doc = new jsPDF();
      
      // Título e informações básicas
      doc.setFontSize(20);
      doc.text('Relatório de Planejamento Financeiro', 105, 15, { align: 'center' });
      
      doc.setFontSize(12);
      doc.text(`Data: ${formatDate()}`, 20, 25);
      
      if (userName) {
        doc.text(`Nome: ${userName}`, 20, 32);
      }
      
      let yPosition = userName ? 42 : 35;
      
      // Notas do usuário
      if (userNotes) {
        doc.setFontSize(12);
        doc.text('Observações:', 20, yPosition);
        
        const splitNotes = doc.splitTextToSize(userNotes, 170);
        doc.text(splitNotes, 20, yPosition + 7);
        
        yPosition += 10 + (splitNotes.length * 7);
      }
      
      // Seção da calculadora
      if (selectedSections.calculator && data.calculatorData) {
        if (yPosition > 200) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(16);
        doc.text('Calculadora de Juros Compostos', 20, yPosition);
        yPosition += 10;
        
        // Parâmetros da calculadora
        doc.setFontSize(12);
        doc.text('Parâmetros:', 20, yPosition);
        yPosition += 7;
        
        // Dados de entrada da calculadora
        const tableCalculatorData = [
          ['Parâmetro', 'Valor']
        ];
        
        if (data.calculatorData.formData) {
          const { formData } = data.calculatorData;
          
          tableCalculatorData.push(
            ['Valor Inicial', formatCurrency(formData.initialCapital)],
            ['Aporte Mensal', formatCurrency(formData.monthlyContribution)],
            ['Taxa de Juros', formatPercentage(formData.interestRate)],
            ['Período', `${formData.period} meses`]
          );
        } else if (data.calculatorData) {
          // Fallback para caso a estrutura seja diferente
          tableCalculatorData.push(
            ['Valor Inicial', formatCurrency(data.calculatorData.initialCapital)],
            ['Aporte Mensal', formatCurrency(data.calculatorData.monthlyContribution)],
            ['Taxa de Juros', formatPercentage(data.calculatorData.interestRate)],
            ['Período', `${data.calculatorData.period} meses`]
          );
        }
        
        const table = autoTable(doc, {
          startY: yPosition,
          head: [tableCalculatorData[0]],
          body: tableCalculatorData.slice(1),
          theme: 'striped',
          headStyles: { fillColor: [63, 81, 181] }
        });
        
        // Usar a posição final da tabela para a próxima seção
        yPosition = table && table.finalY ? table.finalY + 15 : yPosition + 50;
        
        // Dados da calculadora
        if (data.calculatorData.results) {
          doc.setFontSize(16);
          doc.text('Resultados da Calculadora', 20, yPosition);
          yPosition += 10;
          
          // Verificando a estrutura dos dados - pode estar em results.summary
          const results = data.calculatorData.results.summary 
            ? data.calculatorData.results.summary 
            : data.calculatorData.results;
          
          const tableCalculatorResults = [
            ['Parâmetro', 'Valor'],
            ['Montante Final', formatCurrency(results.finalAmount)],
            ['Total Investido', formatCurrency(results.investedAmount || results.totalInvested)],
            ['Lucro', formatCurrency(results.interestAmount || results.profit)],
            ['Rentabilidade', formatPercentage(results.returnRate)]
          ];
          
          const table = autoTable(doc, {
            startY: yPosition,
            head: [tableCalculatorResults[0]],
            body: tableCalculatorResults.slice(1),
            theme: 'striped',
            headStyles: { fillColor: [63, 81, 181] }
          });
          
          // Usar a posição final da tabela para a próxima seção
          yPosition = table && table.finalY ? table.finalY + 15 : yPosition + 50;
        } else if (data.calculatorData.finalAmount) {
          // Fallback caso a estrutura seja diferente
          doc.setFontSize(16);
          doc.text('Resultados da Calculadora', 20, yPosition);
          yPosition += 10;
          
          const tableCalculatorResults = [
            ['Parâmetro', 'Valor'],
            ['Montante Final', formatCurrency(data.calculatorData.finalAmount)],
            ['Total Investido', formatCurrency(data.calculatorData.totalInvested)],
            ['Lucro', formatCurrency(data.calculatorData.profit)],
            ['Rentabilidade', formatPercentage(data.calculatorData.returnRate)]
          ];
          
          const table = autoTable(doc, {
            startY: yPosition,
            head: [tableCalculatorResults[0]],
            body: tableCalculatorResults.slice(1),
            theme: 'striped',
            headStyles: { fillColor: [63, 81, 181] }
          });
          
          // Usar a posição final da tabela para a próxima seção
          yPosition = table && table.finalY ? table.finalY + 15 : yPosition + 50;
        }
      }
      
      // Dados de cenários
      if (selectedSections.scenarios && data.scenariosData) {
        if (yPosition > 200) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(16);
        doc.text('Comparação de Cenários', 20, yPosition);
        
        yPosition += 10;
        
        const scenariosParams = [
          ['Parâmetro', 'Valor'],
          ['Capital Inicial', formatCurrency(data.scenariosData.formData.initialCapital)],
          ['Aporte Mensal', formatCurrency(data.scenariosData.formData.monthlyContribution)],
          ['Período', `${data.scenariosData.formData.period} meses`]
        ];
        
        const table = autoTable(doc, {
          startY: yPosition,
          head: [scenariosParams[0]],
          body: scenariosParams.slice(1),
          theme: 'striped',
          headStyles: { fillColor: [63, 81, 181] }
        });
        
        yPosition = table && table.finalY ? table.finalY + 10 : yPosition + 50;
        
        if (data.scenariosData.results && data.scenariosData.results.length > 0) {
          const scenariosResultsHead = ['Cenário', 'Taxa Mensal', 'Montante Final', 'Rentabilidade'];
          const scenariosResultsBody = data.scenariosData.results.map(scenario => [
            scenario.name,
            formatPercentage(scenario.interestRate),
            formatCurrency(scenario.totalAmount),
            formatPercentage(scenario.returnRate)
          ]);
          
          const table = autoTable(doc, {
            startY: yPosition,
            head: [scenariosResultsHead],
            body: scenariosResultsBody,
            theme: 'striped',
            headStyles: { fillColor: [63, 81, 181] }
          });
          
          yPosition = table && table.finalY ? table.finalY + 15 : yPosition + 50;
        }
      }
      
      // Dados de marcos
      if (selectedSections.milestones && data.milestonesData) {
        if (yPosition > 200) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(16);
        doc.text('Marcos Financeiros', 20, yPosition);
        
        yPosition += 10;
        
        const milestonesParams = [
          ['Parâmetro', 'Valor'],
          ['Capital Inicial', formatCurrency(data.milestonesData.formData.initialCapital)],
          ['Aporte Mensal', formatCurrency(data.milestonesData.formData.monthlyContribution)],
          ['Taxa de Juros', formatPercentage(data.milestonesData.formData.interestRate)]
        ];
        
        const table = autoTable(doc, {
          startY: yPosition,
          head: [milestonesParams[0]],
          body: milestonesParams.slice(1),
          theme: 'striped',
          headStyles: { fillColor: [63, 81, 181] }
        });
        
        yPosition = table && table.finalY ? table.finalY + 10 : yPosition + 50;
        
        if (data.milestonesData.milestones && data.milestonesData.milestones.length > 0) {
          const milestonesResultsHead = ['Objetivo', 'Valor Alvo', 'Tempo Estimado'];
          const milestonesResultsBody = data.milestonesData.milestones.map(milestone => [
            milestone.name,
            formatCurrency(milestone.targetAmount),
            `${milestone.timeToReach} meses (${(milestone.timeToReach/12).toFixed(1)} anos)`
          ]);
          
          const table = autoTable(doc, {
            startY: yPosition,
            head: [milestonesResultsHead],
            body: milestonesResultsBody,
            theme: 'striped',
            headStyles: { fillColor: [63, 81, 181] }
          });
          
          yPosition = table && table.finalY ? table.finalY + 15 : yPosition + 50;
        }
      }
      
      // Dados de aposentadoria
      if (selectedSections.retirement && data.retirementData) {
        if (yPosition > 200) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(16);
        doc.text('Planejamento de Aposentadoria', 20, yPosition);
        
        yPosition += 10;
        
        const retirementParams = [
          ['Parâmetro', 'Valor'],
          ['Idade Atual', `${data.retirementData.currentAge} anos`],
          ['Idade de Aposentadoria', `${data.retirementData.retirementAge} anos`],
          ['Expectativa de Vida', `${data.retirementData.lifeExpectancy} anos`],
          ['Patrimônio Atual', formatCurrency(data.retirementData.currentSavings)],
          ['Aporte Mensal', formatCurrency(data.retirementData.monthlyContribution)],
          ['Taxa de Juros', formatPercentage(data.retirementData.interestRate)],
          ['Inflação Anual', formatPercentage(data.retirementData.inflation)],
          ['Despesa Mensal Desejada', formatCurrency(data.retirementData.monthlyExpenses)]
        ];
        
        const table = autoTable(doc, {
          startY: yPosition,
          head: [retirementParams[0]],
          body: retirementParams.slice(1),
          theme: 'striped',
          headStyles: { fillColor: [63, 81, 181] }
        });
        
        yPosition = table && table.finalY ? table.finalY + 10 : yPosition + 50;
        
        if (data.retirementData.results) {
          const retirementResults = [
            ['Resultado', 'Valor'],
            ['Capital Necessário', formatCurrency(data.retirementData.results.targetAmount)],
            ['Renda Passiva Mensal', formatCurrency(data.retirementData.results.passiveIncome)],
            ['Tempo até Aposentadoria', `${data.retirementData.results.yearsToRetirement.toFixed(1)} anos`]
          ];
          
          const table = autoTable(doc, {
            startY: yPosition,
            head: [retirementResults[0]],
            body: retirementResults.slice(1),
            theme: 'striped',
            headStyles: { fillColor: [63, 81, 181] }
          });
          
          yPosition = table && table.finalY ? table.finalY + 15 : yPosition + 50;
        }
      }
      
      // Rodapé do documento
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text('Calculadora de Juros Compostos - Gerado em ' + formatDate(), 105, 285, { align: 'center' });
      
      // Salvar o PDF
      doc.save('relatorio-financeiro.pdf');
      
      setExportInProgress(false);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      alert('Ocorreu um erro ao exportar o PDF. Por favor, tente novamente.');
      setExportInProgress(false);
    }
  };

  const exportToExcel = () => {
    try {
      setExportInProgress(true);
      const data = getAllData();
      
      // Criar uma nova planilha
      const wb = XLSX.utils.book_new();
      
      // Informações básicas
      const infoData = [
        ['Relatório de Planejamento Financeiro'],
        ['Data', formatDate()],
        ['Nome', userName || 'Não informado']
      ];
      
      if (userNotes) {
        infoData.push(['Observações', userNotes]);
      }
      
      const infoSheet = XLSX.utils.aoa_to_sheet(infoData);
      XLSX.utils.book_append_sheet(wb, infoSheet, 'Informações');
      
      // Dados da calculadora
      if (selectedSections.calculator && data.calculatorData) {
        const calculatorData = [
          ['Calculadora de Juros Compostos'],
          [],
          ['Parâmetro', 'Valor'],
          ['Capital Inicial', data.calculatorData.initialCapital],
          ['Aporte Mensal', data.calculatorData.monthlyContribution],
          ['Taxa de Juros', data.calculatorData.interestRate / 100],
          ['Período (meses)', data.calculatorData.period]
        ];
        
        if (data.calculatorData.results) {
          calculatorData.push(
            [],
            ['Resultados'],
            [],
            ['Montante Final', data.calculatorData.results.finalAmount],
            ['Total Investido', data.calculatorData.results.totalInvested],
            ['Lucro', data.calculatorData.results.profit],
            ['Rentabilidade', data.calculatorData.results.returnRate / 100]
          );
        }
        
        const calculatorSheet = XLSX.utils.aoa_to_sheet(calculatorData);
        XLSX.utils.book_append_sheet(wb, calculatorSheet, 'Calculadora');
      }
      
      // Dados de cenários
      if (selectedSections.scenarios && data.scenariosData) {
        const scenariosBaseData = [
          ['Comparação de Cenários'],
          [],
          ['Parâmetros Base'],
          [],
          ['Capital Inicial', data.scenariosData.formData.initialCapital],
          ['Aporte Mensal', data.scenariosData.formData.monthlyContribution],
          ['Período (meses)', data.scenariosData.formData.period]
        ];
        
        const scenariosSheet = XLSX.utils.aoa_to_sheet(scenariosBaseData);
        
        if (data.scenariosData.results && data.scenariosData.results.length > 0) {
          // Adicionar cabeçalho para resultados
          XLSX.utils.sheet_add_aoa(scenariosSheet, [
            [],
            ['Resultados por Cenário'],
            [],
            ['Cenário', 'Taxa Mensal', 'Montante Final', 'Rentabilidade']
          ], { origin: { r: scenariosBaseData.length, c: 0 } });
          
          // Adicionar resultados dos cenários
          const scenariosResultsData = data.scenariosData.results.map(scenario => [
            scenario.name,
            scenario.interestRate / 100,
            scenario.totalAmount,
            scenario.returnRate / 100
          ]);
          
          XLSX.utils.sheet_add_aoa(scenariosSheet, scenariosResultsData, { 
            origin: { r: scenariosBaseData.length + 4, c: 0 } 
          });
        }
        
        XLSX.utils.book_append_sheet(wb, scenariosSheet, 'Cenários');
      }
      
      // Dados de marcos
      if (selectedSections.milestones && data.milestonesData) {
        const milestonesBaseData = [
          ['Marcos Financeiros'],
          [],
          ['Parâmetros Base'],
          [],
          ['Capital Inicial', data.milestonesData.formData.initialCapital],
          ['Aporte Mensal', data.milestonesData.formData.monthlyContribution],
          ['Taxa de Juros', data.milestonesData.formData.interestRate / 100]
        ];
        
        const milestonesSheet = XLSX.utils.aoa_to_sheet(milestonesBaseData);
        
        if (data.milestonesData.milestones && data.milestonesData.milestones.length > 0) {
          // Adicionar cabeçalho para marcos
          XLSX.utils.sheet_add_aoa(milestonesSheet, [
            [],
            ['Objetivos Financeiros'],
            [],
            ['Objetivo', 'Valor Alvo', 'Tempo Estimado (meses)', 'Tempo Estimado (anos)']
          ], { origin: { r: milestonesBaseData.length, c: 0 } });
          
          // Adicionar dados dos marcos
          const milestonesData = data.milestonesData.milestones.map(milestone => [
            milestone.name,
            milestone.targetAmount,
            milestone.timeToReach,
            milestone.timeToReach / 12
          ]);
          
          XLSX.utils.sheet_add_aoa(milestonesSheet, milestonesData, { 
            origin: { r: milestonesBaseData.length + 4, c: 0 } 
          });
        }
        
        XLSX.utils.book_append_sheet(wb, milestonesSheet, 'Marcos');
      }
      
      // Dados de aposentadoria
      if (selectedSections.retirement && data.retirementData) {
        const retirementBaseData = [
          ['Planejamento de Aposentadoria'],
          [],
          ['Parâmetros'],
          [],
          ['Idade Atual', data.retirementData.currentAge],
          ['Idade de Aposentadoria', data.retirementData.retirementAge],
          ['Expectativa de Vida', data.retirementData.lifeExpectancy],
          ['Patrimônio Atual', data.retirementData.currentSavings],
          ['Aporte Mensal', data.retirementData.monthlyContribution],
          ['Taxa de Juros', data.retirementData.interestRate / 100],
          ['Inflação Anual', data.retirementData.inflation / 100],
          ['Despesa Mensal Desejada', data.retirementData.monthlyExpenses]
        ];
        
        const retirementSheet = XLSX.utils.aoa_to_sheet(retirementBaseData);
        
        if (data.retirementData.results) {
          // Adicionar resultados
          XLSX.utils.sheet_add_aoa(retirementSheet, [
            [],
            ['Resultados da Simulação'],
            [],
            ['Capital Necessário', data.retirementData.results.targetAmount],
            ['Renda Passiva Mensal', data.retirementData.results.passiveIncome],
            ['Anos até Aposentadoria', data.retirementData.results.yearsToRetirement]
          ], { origin: { r: retirementBaseData.length, c: 0 } });
        }
        
        XLSX.utils.book_append_sheet(wb, retirementSheet, 'Aposentadoria');
      }
      
      // Gerar o arquivo Excel
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, 'relatorio-financeiro.xlsx');
      
      setExportInProgress(false);
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      alert('Ocorreu um erro ao exportar o Excel. Por favor, tente novamente.');
      setExportInProgress(false);
    }
  };

  const handleExport = () => {
    if (exportFormat === 'pdf') {
      exportToPDF();
    } else {
      exportToExcel();
    }
  };

  return (
    <div className="export-data-container">
      <h2>Exportar Resultados</h2>
      <p className="export-description">
        Exporte seus dados em diferentes formatos para referência futura ou compartilhamento.
        Selecione as seções que deseja incluir no relatório.
      </p>
      
      <div className="export-form">
        <div className="export-section">
          <h3>Informações Pessoais</h3>
          <div className="form-group">
            <label htmlFor="userName">Seu Nome (opcional)</label>
            <input
              type="text"
              id="userName"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Nome para incluir no relatório"
              className="text-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="userNotes">Observações (opcional)</label>
            <textarea
              id="userNotes"
              value={userNotes}
              onChange={(e) => setUserNotes(e.target.value)}
              placeholder="Adicione notas ou observações ao seu relatório"
              className="text-area"
              rows="3"
            />
          </div>
        </div>
        
        <div className="export-section">
          <h3>Formato de Exportação</h3>
          <div className="format-options">
            <div className="format-option">
              <input
                type="radio"
                id="formatPDF"
                name="exportFormat"
                value="pdf"
                checked={exportFormat === 'pdf'}
                onChange={handleFormatChange}
              />
              <label htmlFor="formatPDF">
                <span className="format-icon">📄</span>
                <span className="format-name">PDF</span>
                <span className="format-desc">Relatório formatado para impressão</span>
              </label>
            </div>
            
            <div className="format-option">
              <input
                type="radio"
                id="formatExcel"
                name="exportFormat"
                value="excel"
                checked={exportFormat === 'excel'}
                onChange={handleFormatChange}
              />
              <label htmlFor="formatExcel">
                <span className="format-icon">📊</span>
                <span className="format-name">Excel</span>
                <span className="format-desc">Planilha editável com os dados</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="export-section">
          <h3>Seções para Incluir</h3>
          <div className="section-checkboxes">
            <div className="section-option">
              <input
                type="checkbox"
                id="includeCalculator"
                checked={selectedSections.calculator}
                onChange={() => handleSectionToggle('calculator')}
              />
              <label htmlFor="includeCalculator">Calculadora</label>
            </div>
            
            <div className="section-option">
              <input
                type="checkbox"
                id="includeScenarios"
                checked={selectedSections.scenarios}
                onChange={() => handleSectionToggle('scenarios')}
              />
              <label htmlFor="includeScenarios">Cenários</label>
            </div>
            
            <div className="section-option">
              <input
                type="checkbox"
                id="includeMilestones"
                checked={selectedSections.milestones}
                onChange={() => handleSectionToggle('milestones')}
              />
              <label htmlFor="includeMilestones">Marcos</label>
            </div>
            
            <div className="section-option">
              <input
                type="checkbox"
                id="includeRetirement"
                checked={selectedSections.retirement}
                onChange={() => handleSectionToggle('retirement')}
              />
              <label htmlFor="includeRetirement">Aposentadoria</label>
            </div>
          </div>
        </div>
        
        <div className="export-actions">
          <button
            className="export-button"
            onClick={handleExport}
            disabled={exportInProgress || !hasExportableData()}
          >
            {exportInProgress ? (
              <span className="loading-text">Exportando...</span>
            ) : (
              <>
                <span className="button-icon">
                  {exportFormat === 'pdf' ? '📄' : '📊'}
                </span>
                <span>Exportar {exportFormat === 'pdf' ? 'PDF' : 'Excel'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportData;
